// Mappers into the redesigned program StructuredData shape.
//   • migrateFlatToProgram — the legacy flat 16-field blob → nested shape.
//   • flyerToProgram        — a data/flyer/*.json extraction → nested shape.
// Both return a normalized StructuredData (deep-filled) so callers/migration
// can merge them safely. `investor` is preserved from the legacy blob (it is
// GMCC-internal and never appears on flyers).

import {
  normalizeStructured,
  EMPTY_STRUCTURED,
  type StructuredData,
  type RateScenario,
} from './topic';

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = v.trim();
    if (!t) return null;
    // Handle magnitude suffixes: "$3M" → 3000000, "1.5M" → 1500000, "750K" → 750000.
    const m = t.match(/([\d.,]+)\s*(k|m|b|million|billion|thousand)?/i);
    if (!m) return null;
    const base = Number(m[1].replace(/,/g, ''));
    if (!Number.isFinite(base)) return null;
    const suffix = (m[2] || '').toLowerCase();
    const mult = suffix.startsWith('b') ? 1e9 : suffix.startsWith('m') ? 1e6 : (suffix === 'k' || suffix === 'thousand') ? 1e3 : 1;
    return base * mult;
  }
  return null;
}
function str(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  return null;
}
function arr(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
}

/** Flatten an arbitrary object into "- key: value" lines for the notes field. */
function flattenToLines(obj: Record<string, unknown>, prefix = ''): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || v === '') continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) {
      out.push(`- ${key}: ${v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('; ')}`);
    } else if (typeof v === 'object') {
      out.push(...flattenToLines(v as Record<string, unknown>, key));
    } else {
      out.push(`- ${key}: ${v}`);
    }
  }
  return out;
}

/** Legacy flat blob → nested program shape. */
export function migrateFlatToProgram(old: unknown): StructuredData {
  const o = (old && typeof old === 'object' ? old : {}) as Record<string, unknown>;
  const hasRate =
    o.rate_pct != null || o.rate_apr_pct != null || o.rate_as_of_date != null || (o.loan_type != null && o.loan_type !== '');
  const scenarios: RateScenario[] = hasRate
    ? [{
        label: '',
        loan_type: str(o.loan_type) ?? '',
        interest_rate: num(o.rate_pct),
        apr: num(o.rate_apr_pct),
        rate_as_of: str(o.rate_as_of_date),
        points: null, index: null, margin: null, caps: null,
        assumptions: {
          ltv: num(o.ltv_max_pct),
          loan_amount: num(o.loan_amount_max_usd),
          credit_score: num(o.fico_min),
          property_type: null, transaction_type: null, occupancy: null, dti: null,
        },
      }]
    : [];
  const notes = [str(o.down_payment_notes), str(o.additional_notes)].filter(Boolean).join('\n\n') || null;
  return normalizeStructured({
    ...EMPTY_STRUCTURED,
    investor: str(o.investor),
    program_name: null,
    promo_tag: null,
    loan_category: str(o.loan_type),
    rate_scenarios: scenarios,
    eligibility: {
      loan_purposes: [],
      occupancy: arr(o.occupancy),
      property_types: arr(o.property_types),
      units: null,
      borrower_types: arr(o.borrower_types),
      states_included: arr(o.states),
      states_excluded: [],
      ltv_max: num(o.ltv_max_pct),
      fico_min: num(o.fico_min),
      dti_max: null,
      loan_amount_min: null,
      loan_amount_max: num(o.loan_amount_max_usd),
      documentation: str(o.documentation),
      reserves: str(o.reserves),
    },
    highlights: [],
    special_features: [],
    notes,
  });
}

/** A data/flyer/*.json extraction → nested program shape (Partial-ish; normalized). */
export function flyerToProgram(flyer: unknown): StructuredData {
  const f = (flyer && typeof flyer === 'object' ? flyer : {}) as Record<string, unknown>;
  const lps = Array.isArray(f.loan_programs) ? f.loan_programs : [];
  const scenarios: RateScenario[] = lps.map((p) => {
    const po = (p && typeof p === 'object' ? p : {}) as Record<string, unknown>;
    const as = (po.assumptions && typeof po.assumptions === 'object' ? po.assumptions : {}) as Record<string, unknown>;
    return {
      label: str(po.name) ?? '',
      loan_type: str(po.loan_type) ?? '',
      interest_rate: num(po.interest_rate),
      apr: num(po.apr),
      rate_as_of: str(po.rate_as_of),
      points: num(po.points),
      index: str(po.index),
      margin: str(po.margin),
      caps: str(po.caps),
      assumptions: {
        ltv: num(as.ltv),
        loan_amount: num(as.loan_amount),
        credit_score: num(as.credit_score),
        property_type: str(as.property_type),
        transaction_type: str(as.transaction_type),
        occupancy: str(as.occupancy),
        dti: num(as.dti),
      },
    };
  });
  const el = (f.eligibility && typeof f.eligibility === 'object' ? f.eligibility : {}) as Record<string, unknown>;
  const otherLines = f.other && typeof f.other === 'object' ? flattenToLines(f.other as Record<string, unknown>) : [];
  const notes = otherLines.length ? otherLines.join('\n') : null;
  return normalizeStructured({
    investor: null, // set from the legacy blob during migration, not the flyer
    program_name: str(f.program_name),
    promo_tag: str(f.promo_tag),
    loan_category: null,
    rate_scenarios: scenarios,
    eligibility: {
      loan_purposes: arr(el.loan_purposes),
      occupancy: arr(el.occupancy),
      property_types: arr(el.property_types),
      units: str(el.units),
      borrower_types: arr(el.borrower_types),
      states_included: arr(el.states_included),
      states_excluded: arr(el.states_excluded),
      ltv_max: num(el.ltv_max),
      fico_min: num(el.fico_min),
      dti_max: num(el.dti_max),
      loan_amount_min: num(el.loan_amount_min),
      loan_amount_max: num(el.loan_amount_max),
      documentation: str(el.documentation),
      reserves: str(el.reserves),
    },
    highlights: arr(f.highlights),
    special_features: arr(f.special_features),
    notes,
  });
}
